import React, {useCallback, useMemo} from "react";
import {issueCreditFromInvoiceItemSelectionDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import ProductInformationPopover from "../../../../app-specific/draggable-popover/product-information-popover";
import {Box, DialogActions, DialogContent, Divider, Typography} from "@mui/material";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {ReactComponent as SuccessSvg} from "../../../../../../assets/images/art-like/quick-order-submitted.svg";
import Button from "../../../../base/button/button";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {Link} from "react-router-dom";
import DateUtils from "../../../../../../core/services/date-utils";
import ToastService from "../../../../../../core/services/toast";
import {exportCSVFile} from "../../../../../../core/services/utils/utils";
import {ProductTypes} from "../../../../../../core/constants/enums";
import {WarningRounded} from "@mui/icons-material";
import {ReactComponent as Passed} from "../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../assets/images/validations/failed.svg";
import useAccessLevels from "../../../../../hooks/use-access-levels";


const dataGridSaveConfig = {
    version: 2,
    name: 'issueCreditFromInvoiceResultsSectionDataGrid',
}

const ResultsSection = ({order, close, invoiceNo, warehouses}) => {

    const operatorAccessLevels = useAccessLevels();
    const canExportCreditsList = operatorAccessLevels.packages.sales.exportCreditsList;

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.status,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                resizable: false,
                width: 0,
            },
            {
                title: "Part No",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
                width: 130,
            },
            {
                title: "Warehouse",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Unit Price",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.unitPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Total Price",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.totalPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Serials / Quantity",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.serialNoOrQuantity,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: "Message",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.message,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 250,
            }
        ], []);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return order?.itemsReport.map(item => {
            const warehouse = warehouses?.find(e => e.code === item.item.warehouseCode);
            return ({
                key: item.item.id,
                data: item.item,
                cells: {
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.status]: item?.resultFlag
                        ? <Passed/>
                        : <Failed/>,
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.warehouse]: !!warehouse ? `${warehouse.name} (${warehouse.code})` : item.item.warehouseCode,
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.partNo]:
                        <ProductInformationPopover partNo={item.item.partNo}>
                            {item.item.partNo ?? ""}
                        </ProductInformationPopover>,
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.unitPrice]: item.item.unitPrice,
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.totalPrice]: Number(item.item.unitPrice * item.item.committedQuantity),
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.serialNoOrQuantity]:
                        item.item.isSerialized
                            ? item.item.serials?.join(', ') ?? ''
                            : !item.item.typeInfo?.code || item.item.typeInfo?.code === ProductTypes.nonPhysical
                                ? "N/A (Non-Physical)"
                                : item.item.committedQuantity,
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.message]: item.message,
                },
            })
        })
    }, [order?.itemsReport, warehouses])

    /**
     * Exports the added reports in the state to csv file
     */
    const exportToCsv = useCallback(() => {
        const exportTitle = `Credit Issued Report For Invoice ${invoiceNo} - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`;
        const toBeExported = order?.itemsReport?.map((record) => {
            return {
                "Part No": record.item.partNo,
                "Warehouse": record.item.warehouseCode,
                "Unit Price": record.item.unitPrice,
                "Total Price": Number(record.item.unitPrice * record.item.committedQuantity),
                "Serials / Quantity": record.item.isSerialized
                    ? record.item.serials?.join(', ') ?? ''
                    : record.item.committedQuantity,
                "Message": record.message,
            }
        }) ?? []
        if (!toBeExported.length) {
            ToastService.warning({message: "No records found for exporting!"});
            return;
        }
        exportCSVFile(toBeExported, exportTitle);
    }, [order?.itemsReport, invoiceNo])

    const compiledWithWarnings = useMemo(() => order?.itemsReport?.some(e => !e.resultFlag) ?? false, [order?.itemsReport]);

    return (
        <>
            <DialogContent>
                <div className={'d-flex flex-column align-items-center px-4'}>
                    <SuccessSvg width={'200px'} height={'200px'} className={'mb-3'}/>
                    <Typography variant={'h6'} fontWeight={700} className={'mb-3'}>
                        The Sales-Order {order?.orderNo ?? ""} for Credit Issued {compiledWithWarnings ? "With Warnings" : "Successfully"}
                    </Typography>
                    <Typography variant={'body2'} className={'mb-3 align-self-start'}>
                        The sales-order for the credit has been issued. You can view the order in a new tab and check the details. Remember to <Typography
                        variant={'body2'} component={'span'} fontWeight={700} color={'primary.main'}>
                        generate the invoice for this sales order
                    </Typography> to complete the credit issuance process.
                    </Typography>
                    {
                        compiledWithWarnings &&
                        <Typography variant={'body2'} className={'mb-3 align-self-start'}>
                            <Typography variant={'body2'} component={'span'} fontWeight={700} color={'warning.main'}>
                                <WarningRounded className={'icon small'}/> Warning:
                            </Typography> Some items could not be processed, please check the details in the table below. You can try to resolve the issues manually in the sales order.
                        </Typography>
                    }
                    {
                        !order?.referenceSet &&
                        <Typography variant={'body2'} className={'mb-3 align-self-start'}>
                            <Typography variant={'body2'} component={'span'} fontWeight={700} color={'warning.main'}>
                                <WarningRounded className={'icon small'}/> Warning:
                            </Typography> The reference number could not be set for the order, please set it manually: <span className={'primary-color-1 font-weight-semi-bold'}>{invoiceNo}</span>
                        </Typography>
                    }
                    <PartnerDataGrid
                        hideFooter
                        getExportInfo={canExportCreditsList ? exportToCsv : undefined}
                        {...(dataGridSaveConfig ?? {})}
                        initialLayoutState={{columns: columns}}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        visibleRows={10}
                        state={{
                            density: dataGridDensity,
                            pagination: {currentPage: 1, pageSize: order?.itemsReport.length ?? 10},
                        }}
                    />
                </div>
            </DialogContent>
            <DialogActions className={'p-0 d-flex flex-column'}>
                <Divider flexItem variant={'fullWidth'}/>
                <Box sx={{marginLeft: '0 !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'8px 0 8px 0'}
                        component={Link}
                        target={'_blank'}
                        to={routeFunctions.customer.sales.salesOrder.single(order?.customerId, order?.orderId ?? "0")}
                    >
                        Open Order In New Tab
                    </Button>
                </Box>
            </DialogActions>
        </>
    );
}

export default ResultsSection;